import { i18n } from "../../../boot/i18n";
import Invoice, { INVOICE_SETTINGS } from "../../../classes/Invoice";
import SaleItem from "../../../classes/SaleItem";
import SaleLabor from "../../../classes/SaleLabor";
import SaleMiscItem from "../../../classes/SaleMiscItem";
import CustomerInvoice from "../../../classes/CustomerInvoice";
import DocumentRef from "../../../classes/DocumentRef";
import { db } from "../../../boot/firebase";
import {
  collection,
  deleteField,
  doc,
  documentId,
  getDoc,
  getDocs,
  query,
  runTransaction,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { call, roundPrice } from "../../../util/misc";
import { cloneDeep, compact, forEach, get, isArray, uniq } from "lodash";

// Invoices are currently delivered to customers via QuickBooks, so this app
// must never email them automatically. When true, the customer invoice
// notification is suppressed regardless of the `noNotify` argument. Flip this
// to false (and restore the "Do not notify" opt-in checkboxes) once in-app
// payment collection is enabled.
const SUPPRESS_CUSTOMER_INVOICE_NOTIFICATIONS = true;

// Mirrors DOWN_PAYMENT_TYPES in functions/downPaymentAllocation.js.
const DOWN_PAYMENT_TX_TYPES = ["down-payment", "deferred-down-payment"];

/**
 * Query the prior down-payment and sale-payment allocations for a sale, and
 * derive the still-available pool of customer credit that can be applied to a
 * new invoice. The pool combines two sources:
 *
 *   1. Unallocated `sale-payment` pool — sale-payment txs minus the portion
 *      already recorded on prior invoices via `salePaymentsApplied`.
 *   2. Overpayments sitting on existing active invoices — any invoice with
 *      `total.balance < 0` represents customer credit that has been collected
 *      but not allocated to any due amount, so we can reclaim it onto the new
 *      invoice (see `overpaidInvoices` in the return value).
 *
 * It also derives `availableDownPaymentFee`: the card/processing fee taken on
 * down-payment txs that no other active invoice already carries, either as a
 * stamped `total.downPaymentFee` or as allocator-recorded
 * `downPaymentFeeApplied`. Only that may be stamped on the new invoice.
 * Stamping the sale's whole `total.fee` (which also holds sale-payment and
 * invoice-payment fees the allocators record in `total.fee`) charged the same
 * fee on every later invoice (prod 03500063A: one $168.04 fee on three
 * invoices).
 *
 * The caller uses this with `computeInvoiceTotalWithAllocations()` to compute
 * the new invoice's totals, and (for non-buffer flows) to write the reclaim
 * adjustments back onto the overpaid invoices.
 *
 * Shared by `CREATE_AND_SEND_INVOICE` (auto-invoice on delivery / pickup / QC)
 * and the manual `InvoiceEdit` dialog so both flows allocate identically.
 *
 * @param {Object} params
 * @param {string} params.projectID
 * @param {string} params.saleID
 * @param {string=} params.excludeInvoiceID When editing an existing invoice,
 *                                          its prior contribution is excluded.
 * @returns {Promise<{
 *   priorDownPaymentAllocated: number,
 *   priorSalePaymentAllocated: number,
 *   totalSalePaymentPool: number,
 *   availableSalePaymentPool: number,
 *   availableDownPaymentFee: number,
 *   reclaimableOverpaymentTotal: number,
 *   overpaidInvoices: Array<{
 *     id: string,
 *     ref: import("firebase/firestore").DocumentReference,
 *     customerRef: import("firebase/firestore").DocumentReference|null,
 *     overpayment: number,
 *     payments: number,
 *     salePaymentsApplied: number,
 *     balance: number,
 *   }>,
 * }>}
 */
export const getInvoiceAllocationPriors = async ({
  projectID,
  saleID,
  excludeInvoiceID = null,
}) => {
  // Sum down payment and sale-payments already allocated to existing invoices,
  // and collect any active invoices with negative balances (overpayments).
  const existingInvoices = await getDocs(
    collection(db, Invoice.getPath(projectID, saleID))
  );
  let priorDownPaymentAllocated = 0;
  let priorSalePaymentAllocated = 0;
  let priorTaxAllocated = 0;
  let priorDownPaymentFeeAllocated = 0;
  const overpaidInvoices = [];
  existingInvoices.docs.forEach((invoiceDoc) => {
    const inv = invoiceDoc.data();
    if (inv.voidedDate) return;
    if (excludeInvoiceID && invoiceDoc.id === excludeInvoiceID) {
      // An edit recomputes this invoice's `total.downPaymentFee` but keeps
      // its `total.fee` (see Invoice#computeTotal), so a down-payment fee the
      // allocator recorded there is still billed here.
      priorDownPaymentFeeAllocated += inv.downPaymentFeeApplied || 0;
      return;
    }
    priorDownPaymentAllocated += inv.total?.downPayment || 0;
    priorDownPaymentFeeAllocated +=
      (inv.total?.downPaymentFee || 0) + (inv.downPaymentFeeApplied || 0);
    priorSalePaymentAllocated += inv.salePaymentsApplied || 0;
    // Sum tax already billed on prior invoices so a new invoice can snap its
    // own rounded tax to make the running total match the sale-level tax
    // exactly (see the reconciliation in InvoiceTotal.computeTotal).
    priorTaxAllocated += inv.total?.tax || 0;
    const balance = roundPrice(inv.total?.balance || 0);
    if (balance < 0) {
      const customerID = inv.billTo?.address?.contact;
      overpaidInvoices.push({
        id: invoiceDoc.id,
        ref: invoiceDoc.ref,
        customerRef: customerID
          ? doc(db, `c/${customerID}/invoices/${invoiceDoc.id}`)
          : null,
        overpayment: roundPrice(-balance),
        payments: roundPrice(inv.total?.payments || 0),
        salePaymentsApplied: roundPrice(inv.salePaymentsApplied || 0),
        balance,
      });
    }
  });
  // Deterministic order so split allocations are stable across runs.
  overpaidInvoices.sort((a, b) => String(a.id).localeCompare(String(b.id)));
  // Only keep a customer mirror ref when the mirror exists: a credit memo has
  // a negative balance too but never gets a `c/{customer}/invoices` mirror, and
  // batch.update() on a missing document fails the whole invoice save with
  // NOT_FOUND. Same rule the down-payment function applies to its mirrors.
  await Promise.all(
    overpaidInvoices.map(async (inv) => {
      if (inv.customerRef && !(await getDoc(inv.customerRef)).exists()) {
        inv.customerRef = null;
      }
    })
  );
  const reclaimableOverpaymentTotal = roundPrice(
    overpaidInvoices.reduce((sum, inv) => sum + inv.overpayment, 0)
  );

  // Query sale-payments for this sale; any portion not already allocated to a
  // prior invoice is available to apply to this one. Down-payment txs come
  // along only for their fee (see `availableDownPaymentFee` above).
  const paymentSnapshot = await getDocs(
    query(
      collection(db, "tx"),
      where("type", "in", ["sale-payment", ...DOWN_PAYMENT_TX_TYPES]),
      where("for", "==", projectID + saleID)
    )
  );

  let totalSalePaymentPool = 0;
  let downPaymentFeePool = 0;
  paymentSnapshot.docs.forEach((doc) => {
    const txData = doc.data();
    if (DOWN_PAYMENT_TX_TYPES.includes(txData.type)) {
      downPaymentFeePool += txData.fee || 0;
      return;
    }
    totalSalePaymentPool += roundPrice(
      (txData.amount || 0) - (txData.discount || 0) + (txData.fee || 0)
    );
  });
  const availableSalePaymentPool = Math.max(
    0,
    roundPrice(totalSalePaymentPool - priorSalePaymentAllocated)
  );
  const availableDownPaymentFee = Math.max(
    0,
    roundPrice(downPaymentFeePool - priorDownPaymentFeeAllocated)
  );

  return {
    priorDownPaymentAllocated,
    priorSalePaymentAllocated,
    priorTaxAllocated,
    totalSalePaymentPool,
    availableSalePaymentPool,
    availableDownPaymentFee,
    reclaimableOverpaymentTotal,
    overpaidInvoices,
  };
};

/**
 * Compute an invoice's total against the sale, applying any available
 * customer credit (unused sale-payment pool + overpayments reclaimed from
 * existing invoices) capped at the invoice's pre-payment due amount.
 *
 * Reclaimable overpayments are consumed first so the source invoices can be
 * brought back to a $0 balance. Any remainder is drawn from the unallocated
 * sale-payment pool. The caller is responsible for writing the
 * `overpaidInvoiceUpdates` against the source invoices to actually move the
 * credit; the in-memory `invoice.total.payments` already reflects the full
 * `invoicePaymentAmount` (sale-pool + reclaim).
 *
 * @param {Object} params
 * @param {Invoice} params.invoice  Invoice/buffer to compute against
 * @param {Sale}    params.sale     Sale that the invoice belongs to
 * @param {number}  params.priorDownPaymentAllocated
 * @param {number}  params.availableSalePaymentPool
 * @param {number}  [params.availableDownPaymentFee=0] From getInvoiceAllocationPriors
 * @param {Array}   [params.overpaidInvoices=[]]    From getInvoiceAllocationPriors
 * @returns {{
 *   total: Object,
 *   invoicePaymentAmount: number,
 *   salePaymentsApplied: number,
 *   overpaymentReclaimed: number,
 *   overpaidInvoiceUpdates: Array<{
 *     id: string,
 *     ref: import("firebase/firestore").DocumentReference,
 *     customerRef: import("firebase/firestore").DocumentReference|null,
 *     reclaim: number,
 *     newPayments: number,
 *     newSalePaymentsApplied: number,
 *     newBalance: number,
 *   }>,
 * }}
 */
export const computeInvoiceTotalWithAllocations = ({
  invoice,
  sale,
  priorDownPaymentAllocated,
  priorTaxAllocated = 0,
  availableSalePaymentPool,
  availableDownPaymentFee = 0,
  overpaidInvoices = [],
}) => {
  const reclaimableOverpaymentTotal = roundPrice(
    overpaidInvoices.reduce((sum, inv) => sum + (inv.overpayment || 0), 0)
  );
  const totalAvailable = roundPrice(
    availableSalePaymentPool + reclaimableOverpaymentTotal
  );

  let invoicePaymentAmount = 0;
  if (totalAvailable > 0) {
    // Compute base total without any payment credit to get the due amount.
    const baseTotal = invoice.computeTotal({
      quoteSubtotal: sale.data.total.subtotal,
      quoteDiscount: sale.data.total.discount,
      quotePrice: sale.data.total.price,
      downPaymentAmount: sale.data.downPaymentAmount,
      downPaymentFee: availableDownPaymentFee,
      priorDownPaymentAllocated,
      priorTaxAllocated,
      quoteTax: sale.data.total.tax,
    });
    invoicePaymentAmount = Math.max(
      0,
      roundPrice(Math.min(totalAvailable, baseTotal.due))
    );
  }

  // Drain reclaimable overpayments first (so source invoices return to $0
  // balance), then draw the remainder from the unallocated sale-payment pool.
  let remainingReclaim = Math.min(
    invoicePaymentAmount,
    reclaimableOverpaymentTotal
  );
  const overpaymentReclaimed = roundPrice(remainingReclaim);
  const salePaymentsApplied = roundPrice(
    invoicePaymentAmount - overpaymentReclaimed
  );

  const overpaidInvoiceUpdates = [];
  for (const inv of overpaidInvoices) {
    if (remainingReclaim <= 0) break;
    const reclaim = roundPrice(
      Math.min(remainingReclaim, inv.overpayment || 0)
    );
    if (reclaim <= 0) continue;
    remainingReclaim = roundPrice(remainingReclaim - reclaim);
    const newPayments = roundPrice((inv.payments || 0) - reclaim);
    const newBalance = roundPrice((inv.balance || 0) + reclaim);
    // Reduce salePaymentsApplied proportionally so the unallocated pool grows
    // back to reflect that this credit is no longer recorded on this invoice.
    const newSalePaymentsApplied = roundPrice(
      Math.max(0, (inv.salePaymentsApplied || 0) - reclaim)
    );
    overpaidInvoiceUpdates.push({
      id: inv.id,
      ref: inv.ref,
      customerRef: inv.customerRef,
      reclaim,
      newPayments,
      newSalePaymentsApplied,
      newBalance,
    });
  }

  const total = invoice.computeTotal({
    quoteSubtotal: sale.data.total.subtotal,
    quoteDiscount: sale.data.total.discount,
    quotePrice: sale.data.total.price,
    downPaymentAmount: sale.data.downPaymentAmount,
    downPaymentFee: availableDownPaymentFee,
    priorDownPaymentAllocated,
    priorTaxAllocated,
    quoteTax: sale.data.total.tax,
    invoicePaymentAmount,
  });

  return {
    total,
    invoicePaymentAmount,
    salePaymentsApplied,
    overpaymentReclaimed,
    overpaidInvoiceUpdates,
  };
};

export const GET_INVOICE = ({ dispatch }, { projectID, saleID, invoiceID }) => {
  return dispatch("GET_DOC", {
    converter: Invoice,
    path: Invoice.getPath(projectID, saleID, invoiceID),
    stateKey: "invoices",
    id: invoiceID,
  });
};

export const GET_INVOICES = (
  { dispatch },
  { projectID, saleID, conditions, limitCount, pagination }
) => {
  const path = projectID && saleID ? Invoice.getPath(projectID, saleID) : null;
  return dispatch("GET_COLLECTION", {
    converter: Invoice,
    stateKey: "invoices",
    colGroup: !path ? "invoices" : null,
    path,
    conditions,
    limitCount,
    pagination,
  });
};

export const LISTEN_INVOICE = async (
  { dispatch },
  { projectID, saleID, invoiceID, next, error, unlisten }
) => {
  return dispatch("LISTEN_DOC", {
    converter: Invoice,
    path: Invoice.getPath(projectID, saleID, invoiceID),
    stateKey: "invoices",
    id: invoiceID,
    next,
    error,
    unlisten,
  });
};

/**
 * `where` clauses that restrict an `invoices` collection-group query to the
 * project invoices at `projects/{projectID}/sales/{saleID}/invoices/{id}`.
 *
 * The customer-facing mirrors at `c/{customerID}/invoices/{id}` live in the
 * same collection group, share document IDs with the project invoices, and
 * carry their own `total`. `Invoice.fromFirestore` derives the project and
 * sale IDs from the document path, so an unbounded group listen would crash on
 * a mirror (and, if it didn't, would overwrite the real invoice in state).
 *
 * Firestore requires `documentId()` bounds on a collection-group query to be
 * full document paths (an even number of segments), hence the sentinel second
 * segment on each bound.
 */
export const projectInvoiceBounds = () => [
  [documentId(), ">=", "projects/\u0000"],
  [documentId(), "<", "projects/\uf8ff"],
];

const toConditionList = (conditions) => {
  if (!conditions || !conditions.length) return [];
  return isArray(conditions[0]) ? conditions : [conditions];
};

export const LISTEN_INVOICES = (
  { dispatch },
  {
    projectID,
    saleID,
    conditions,
    where: whereClause,
    listenerPath,
    error,
    next,
    unlisten,
  }
) => {
  // Accept `where` as an alias for `conditions`, like LISTEN_COLLECTION does —
  // CommissionView passes its documentId() filter that way, and dropping it
  // here would open an unfiltered collection-group listen.
  conditions = conditions ?? whereClause;
  const path = projectID && saleID ? Invoice.getPath(projectID, saleID) : null;
  return dispatch("LISTEN_COLLECTION", {
    converter: Invoice,
    stateKey: "invoices",
    colGroup: !path ? "invoices" : null,
    listenerPath,
    path,
    conditions: path
      ? conditions
      : [...toConditionList(conditions), ...projectInvoiceBounds()],
    next,
    error,
    unlisten,
  });
};

// The claim `getInvoiceID` records on a delivery/pickup event so two users
// completing the same event cannot each invoice it: the event, the sale, and
// every sale-item / labor / misc id the invoice bills. The cloud function
// refuses the number when that event+sale already has a live or in-flight
// invoice for any of the same ids (sale 04100074A got two identical invoices
// from two users six seconds apart, 2026-09-21). Null for anything that is
// not an event-driven invoice, which keeps manual and installation invoicing
// exactly as before.
export function buildInvoiceClaim({ projectID, saleID, event, invoice }) {
  const type = event && event.data && event.data.type;
  if (!event || !["delivery", "pickup"].includes(type)) {
    return null;
  }
  const data = (invoice && invoice.data) || {};
  return {
    projectID,
    saleID,
    eventID: event.id,
    keys: uniq([
      ...Object.keys(data.saleItems || {}),
      ...(data.saleLabors || []),
      ...(data.miscItems || []),
    ]),
  };
}

export const SAVE_INVOICE = async (
  { dispatch, state },
  { projectID, saleID, invoice, claim = null }
) => {
  const batch = writeBatch(db);

  const saveDocuments = (existingData) => {
    return Promise.all([
      dispatch("SAVE_DOCUMENTS", {
        batch,
        parentData: invoice.data,
        fieldPath: "document",
        folderPath: Invoice.getPath(projectID, saleID),
        existingData,
        isSingle: true,
      }),
      dispatch("SAVE_DOCUMENTS", {
        batch,
        parentData: invoice.data,
        fieldPath: "aiaDocument",
        folderPath: Invoice.getPath(projectID, saleID),
        existingData,
        isSingle: true,
      }),
    ]);
  };

  const updateSale = async (changes = {}) => {
    return dispatch("UPDATE_SALE_STATUS", {
      batch,
      projectID,
      saleID,
      changes: {
        ...changes,
        [`status.invoices.${invoice.id}.events`]: invoice.data.events,
        [`status.invoices.${invoice.id}.fullyCollected`]:
          !invoice.data.total.balance,
      },
    });
  };

  if (!invoice.id) {
    // New Invoice
    const now = new Date();
    invoice.data.created = now;
    invoice.data.createdBy = state.user.id;

    // Get unique incremental ID. With a claim the function also refuses the
    // number when the event is already invoiced for these items.
    invoice.id = claim
      ? await call("getInvoiceID", claim)
      : await call("getInvoiceID");

    // Create PDF
    const { pdfFile } = await invoice.createPDF();
    invoice.data.document = new DocumentRef({
      type: "attachment",
      file: { toSave: pdfFile },
    }).data;
    await saveDocuments();

    // Save Invoice
    batch.set(doc(db, invoice.path).withConverter(Invoice), invoice);

    // Save Customer Invoice
    const customerInvoice = invoice.toCustomerInvoice();
    const customerInvoiceRef = doc(db, customerInvoice.path).withConverter(
      CustomerInvoice
    );
    batch.set(customerInvoiceRef, customerInvoice);

    await updateSale();
    await batch.commit();
  } else {
    // Existing Invoice
    invoice.data.modified = new Date();
    invoice.data.modifiedBy = state.user.id;
    const current = await dispatch("GET_INVOICE", {
      projectID,
      saleID,
      invoiceID: invoice.id,
    });

    // Replace PDF
    const { pdfFile } = await invoice.createPDF();
    invoice.data.document = new DocumentRef({
      type: "attachment",
      file: { toSave: pdfFile },
    }).data;
    await saveDocuments(current.data);

    // Save Invoice
    batch.set(doc(db, invoice.path).withConverter(Invoice), invoice);

    // Save Customer Invoice
    const customerInvoice = invoice.toCustomerInvoice();
    const customerInvoiceRef = doc(db, customerInvoice.path).withConverter(
      CustomerInvoice
    );
    batch.set(customerInvoiceRef, customerInvoice);

    await updateSale();
    await batch.commit();
  }
  return invoice;
};

export const DELETE_INVOICE = async (
  { dispatch },
  { projectID, saleID, invoiceID }
) => {
  const batch = writeBatch(db);
  batch.delete(doc(db, Invoice.getPath(projectID, saleID, invoiceID)));
  await dispatch("UPDATE_SALE_STATUS", {
    batch,
    projectID,
    saleID,
    changes: {
      [`status.invoices.${invoiceID}`]: deleteField(),
    },
  });
  return batch.commit();
};

export const VOID_INVOICE = async (
  { dispatch },
  { projectID, saleID, invoiceID, reason, noNotify }
) => {
  await call("voidInvoice", { projectID, saleID, invoiceID, reason, noNotify });
  const batch = writeBatch(db);
  await dispatch("UPDATE_SALE_STATUS", {
    batch,
    projectID,
    saleID,
    changes: {
      [`status.invoices.${invoiceID}`]: deleteField(),
    },
  });
  return batch.commit();
};

/**
 * Resolve whether invoices for a sale should show itemized (per-line) pricing.
 *
 * Invoices inherit this from the sale's final quote: if the final quote had
 * itemized pricing disabled (`settings.itemPrices === false`), invoices
 * generated for that sale should also hide per-line prices. Any other value
 * (including a missing setting, which defaults to itemized) keeps prices
 * visible. The resolved boolean is persisted on the invoice's `settings` so
 * PDF regeneration stays stable regardless of later quote edits.
 *
 * @param {Object} params
 * @param {import("../../../classes/Sale").default} params.sale
 * @returns {Promise<boolean>} true when per-line prices should be shown
 */
export const GET_INVOICE_ITEM_PRICES = async ({ dispatch }, { sale }) => {
  if (!sale) {
    return true;
  }
  let quote = sale.quote;
  if (!quote && sale.data.leadID && sale.data.quoteID) {
    try {
      quote = await dispatch("GET_QUOTE", {
        salesLeadID: sale.data.leadID,
        quoteID: sale.data.quoteID,
      });
    } catch (error) {
      quote = null;
    }
  }
  return get(quote, "data.settings.itemPrices") === false ? false : true;
};

export const CREATE_AND_SEND_INVOICE = async (
  { commit, dispatch, state },
  { projectID, saleID, eventID, items, packingSlip, noNotify }
) => {
  const batch = writeBatch(db);
  const sale = await dispatch("GET_SALE", {
    projectID,
    saleID,
    forceUpdate: true,
  });
  let event = null;

  const data = {};
  const labors = {};

  const refreshInvoiceItemData = async () => {
    // Always re-fetch the latest item/misc data from Firestore before the
    // invoice totals are computed — the store copy can be stale (e.g. pricing
    // edited after the item was first loaded), and this "refresh" step exists
    // precisely to avoid invoicing off stale line data. `getDoc` (not a
    // transaction) so it can still be served from the offline cache. Docs are
    // accumulated and committed in a single SET_MANY instead of one SET per
    // item, collapsing N re-render passes into one.
    const saleItemDocs = [];
    const miscItemDocs = [];

    await Promise.all(
      Object.keys(data.saleItems || {}).map((id) =>
        getDoc(
          doc(db, SaleItem.getPath(projectID, saleID, id)).withConverter(
            SaleItem
          )
        ).then((snapshot) => {
          const itemData = snapshot.data();
          if (itemData) {
            saleItemDocs.push({ stateID: snapshot.id, doc: itemData });
          }
        })
      )
    );

    await Promise.all(
      (data.miscItems || []).map((id) =>
        getDoc(
          doc(db, SaleMiscItem.getPath(projectID, saleID, id)).withConverter(
            SaleMiscItem
          )
        ).then((snapshot) => {
          const itemData = snapshot.data();
          if (itemData) {
            miscItemDocs.push({ stateID: snapshot.id, doc: itemData });
          }
        })
      )
    );

    if (saleItemDocs.length) {
      commit("SET_MANY", { stateKey: "saleItems", docs: saleItemDocs });
    }
    if (miscItemDocs.length) {
      commit("SET_MANY", { stateKey: "miscItems", docs: miscItemDocs });
    }
  };

  if (eventID) {
    data.events = [eventID];
    event = await dispatch("GET_EVENT", eventID);
  }

  const recipients = [];

  if (sale) {
    if (!noNotify && !SUPPRESS_CUSTOMER_INVOICE_NOTIFICATIONS) {
      const billToContact = sale.soldTo && sale.soldTo.contact;
      if (billToContact && billToContact.data.emails.length) {
        recipients.push({
          contact: billToContact.id,
          email: billToContact.data.emails[0],
        });
      }

      if (!recipients.length) {
        dispatch("NOTIFY_ERROR", "invoiceNoEmail");
      }
    }

    data.location = sale.assignedTo[0]?.data.locations[0];
    data.billTo = cloneDeep(sale.soldTo.data);
    data.shipTo = cloneDeep(sale.shipTo.data);
    data.notax = sale.data.notax;
    data.tax = sale.data.tax;
    data.terms = sale.data.terms;
  } else {
    throw "Sale not found";
  }

  if (event) {
    if (event.data.type === "delivery" || event.data.type === "pickup") {
      // Delivery or Pickup

      data.invoiceDate = event.end;
      data.saleItems = { ...(items || event.data.items) };
      // Labor IDs of the items actually being invoiced, collected from the
      // authoritative Firestore read below.
      const invoicedLabors = [];
      // Update quantityInvoiced for each item
      await runTransaction(db, async (tx) => {
        const items = {};
        // Get item data
        await Promise.all(
          Object.keys(data.saleItems).map(async (id) => {
            const ref = doc(
              db,
              `projects/${projectID}/sales/${saleID}/items/${id}`
            );
            const itemData = (await tx.get(ref)).data();
            // An ID that resolves to nothing does not belong to this sale —
            // on a combined delivery the caller may hand us the whole
            // loaded-item map. Drop it rather than dereferencing undefined.
            if (!itemData) {
              delete data.saleItems[id];
              return;
            }
            // Exclude fully invoiced items
            if (itemData.fullyInvoiced) {
              delete data.saleItems[id];
            } else {
              items[id] = { ref, data: itemData };
              invoicedLabors.push(itemData.labor);
            }
          })
        );
      });

      // Aggregate installations for delivered items. Derived from the items
      // actually being invoiced rather than `event.items`, which on a combined
      // delivery is the primary sale's item list and would pull another sale's
      // labor into this sale's invoice.
      let labors = uniq(compact(invoicedLabors));
      if (labors.length) {
        data.saleLabors = [];
        await Promise.all(
          sale.data.status.install.events.map(async (id) => {
            let installation = await dispatch("GET_EVENT", id);
            // Include SaleLabor
            if (
              installation.data.items.some((laborID) =>
                labors.includes(laborID)
              )
            ) {
              data.events.push(id);
              data.saleLabors = uniq(
                data.saleLabors.concat(installation.data.items)
              );
            }
          })
        );
        await Promise.all(
          data.saleLabors.map((laborID) =>
            dispatch("GET_SALE_LABOR", { projectID, saleID, laborID })
          )
        );
      }
    } else if (event.data.type === "installation") {
      // Installation

      data.invoiceDate = new Date();
      data.saleLabors = [];
      data.saleItems = {};

      // Get uninvoiced installations
      (
        await getDocs(
          query(
            collection(
              db,
              `projects/${projectID}/sales/${saleID}/labor`
            ).withConverter(SaleLabor),
            where("status.fullyInvoiced", "!=", true)
          )
        )
      ).docs.forEach((doc) => {
        const labor = doc.data();
        if (labor.status.isFullyInstalled) {
          labors[doc.id] = {
            ref: doc.ref,
            data: labor.data,
            labor,
          };
          data.saleLabors.push(labor.id);
        }
      });

      // Get uninvoiced items
      const items = {};
      (
        await getDocs(
          query(
            collection(
              db,
              `projects/${projectID}/sales/${saleID}/items`
            ).withConverter(SaleItem),
            where("fullyInvoiced", "!=", true)
          )
        )
      ).docs.forEach((doc) => {
        const item = {
          ref: doc.ref,
          data: doc.data(),
        };
        commit("SET", { stateKey: "saleItems", doc: item.data });
        // Determine quantity to invoice
        if (
          item.data.fullyDelivered ||
          Object.values(labors).some(({ data }) => data.items.includes(doc.id))
        ) {
          items[doc.id] = item;
          data.saleItems[doc.id] =
            item.data.quantity - (item.data.quantityInvoiced || 0);
        }
      });
    }

    // Invoice for Delivery, Pickup, or Installation
    // Include uninvoiced misc items
    data.miscItems = [];
    (
      await getDocs(
        query(
          collection(
            db,
            `projects/${projectID}/sales/${saleID}/misc`
          ).withConverter(SaleMiscItem),
          where("fullyInvoiced", "!=", true)
        )
      )
    ).docs.forEach((doc) => {
      data.miscItems.push(doc.id);
      commit("SET", { stateKey: "miscItems", doc: doc.data() });
    });
  } else if (packingSlip) {
    // Invoice for Direct Ship QC
    data.invoiceDate = packingSlip.receivedDate;
    data.saleItems = {};

    // Update quantityInvoiced for each item
    await runTransaction(db, async (tx) => {
      // Set invoice quantity to QC quantity ready
      forEach(packingSlip.data.items, async ({ ready }, id) => {
        if (!ready) {
          return;
        }
        data.saleItems[id] = ready;
      });
    });

    // Include uninvoiced misc items
    data.miscItems = [];
    (
      await getDocs(
        query(
          collection(
            db,
            `projects/${projectID}/sales/${saleID}/misc`
          ).withConverter(SaleMiscItem),
          where("fullyInvoiced", "!=", true)
        )
      )
    ).docs.forEach((doc) => {
      data.miscItems.push(doc.id);
      commit("SET", { stateKey: "miscItems", doc: doc.data() });
    });
  }

  await refreshInvoiceItemData();

  // Inherit itemized-pricing visibility from the sale's final quote.
  const itemPrices = await dispatch("GET_INVOICE_ITEM_PRICES", { sale });
  data.settings = { ...INVOICE_SETTINGS, itemPrices };

  let invoice = new Invoice(projectID, saleID, null, data);

  // Apply prior down payment + available sale-payment pool + any reclaimable
  // overpayments from existing invoices to this invoice.
  const {
    priorDownPaymentAllocated,
    priorTaxAllocated,
    availableSalePaymentPool,
    availableDownPaymentFee,
    overpaidInvoices,
  } = await getInvoiceAllocationPriors({ projectID, saleID });
  const { total, salePaymentsApplied, overpaidInvoiceUpdates } =
    computeInvoiceTotalWithAllocations({
      invoice,
      sale,
      priorDownPaymentAllocated,
      priorTaxAllocated,
      availableSalePaymentPool,
      availableDownPaymentFee,
      overpaidInvoices,
    });
  invoice.data.total = total;
  if (salePaymentsApplied > 0) {
    invoice.data.salePaymentsApplied = salePaymentsApplied;
  }

  if (!invoice.items.length) {
    return;
  }

  // Save Invoice
  invoice = await dispatch("SAVE_INVOICE", {
    projectID,
    saleID,
    invoice,
    claim: buildInvoiceClaim({ projectID, saleID, event, invoice }),
  });

  // Apply reclaim updates to overpaid source invoices so their balances return
  // to $0 (the credit has been moved onto the new invoice via total.payments).
  if (overpaidInvoiceUpdates.length) {
    const modified = new Date();
    const modifiedBy = state.user.id;
    overpaidInvoiceUpdates.forEach((update) => {
      const invoiceUpdate = {
        modified,
        modifiedBy,
        locked: !update.newBalance,
        "total.payments": update.newPayments,
        "total.balance": update.newBalance,
      };
      if (update.newSalePaymentsApplied > 0) {
        invoiceUpdate.salePaymentsApplied = update.newSalePaymentsApplied;
      } else {
        invoiceUpdate.salePaymentsApplied = deleteField();
      }
      batch.update(update.ref, invoiceUpdate);
      if (update.customerRef) {
        batch.update(update.customerRef, {
          modified,
          modifiedBy,
          "total.payments": update.newPayments,
          "total.balance": update.newBalance,
        });
      }
    });
  }

  await batch.commit();

  // Send Invoice (but don't wait for the email to go through)
  dispatch("SEND_INVOICE", {
    invoice,
    recipients,
    subject: i18n.global.t("message.sendInvoice.subject", {
      po: projectID + saleID,
      id: invoice.id,
    }),
    message: i18n.global.t("message.sendInvoice.body", {
      po: projectID + saleID,
      id: invoice.id,
    }),
    replyTo: sale.assignedTo[0]?.emails[0]?.address,
  }).catch((error) => dispatch("NOTIFY_ERROR", error));

  return invoice;
};

export const REGENERATE_INVOICE_PDF = async (
  { dispatch, state },
  { projectID, saleID, invoiceID }
) => {
  const invoice = state.invoices[invoiceID];
  if (!invoice) {
    throw new Error("Invoice not found");
  }

  const { pdfFile } = await invoice.createPDF();
  const newDocument = new DocumentRef({
    type: "attachment",
    file: { toSave: pdfFile },
  }).data;

  const modified = new Date();

  await dispatch("SAVE_DOCUMENTS", {
    parentData: { document: newDocument },
    fieldPath: "document",
    folderPath: Invoice.getPath(projectID, saleID),
    existingData: invoice.data,
    isSingle: true,
  });

  const invoiceRef = doc(db, invoice.path);
  await updateDoc(invoiceRef, {
    document: newDocument,
    modified,
    modifiedBy: state.user.id,
  });

  const customerInvoicePath = `c/${invoice.data.billTo.address.contact}/invoices/${invoiceID}`;
  const customerInvoiceRef = doc(db, customerInvoicePath);
  const customerInvoiceSnapshot = await getDoc(customerInvoiceRef);
  if (customerInvoiceSnapshot.exists()) {
    await updateDoc(customerInvoiceRef, {
      document: newDocument,
      modified,
      modifiedBy: state.user.id,
    });
  }

  return invoice;
};

// Assess a late-payment finance charge on a past-due invoice. The cloud
// callable applies the charge (and any already-accrued backlog) to the
// invoice, its customer mirror, and the sale; afterwards we refresh the store
// copy and regenerate the PDF so a resend / notice reflects the new charge.
export const ASSESS_FINANCE_CHARGE = async (
  { dispatch },
  { projectID, saleID, invoiceID, startDate, rate }
) => {
  const result = await call("assessFinanceCharge", {
    projectID,
    saleID,
    invoiceID,
    // Optional overrides: an earlier accrual start (bypassing the grace
    // period) and/or a custom monthly rate. `startDate` is sent as epoch ms so
    // it survives the callable serialization cleanly.
    startDate: startDate ? new Date(startDate).getTime() : undefined,
    rate: rate || undefined,
  });
  await dispatch("GET_INVOICE", { projectID, saleID, invoiceID });
  await dispatch("REGENERATE_INVOICE_PDF", { projectID, saleID, invoiceID });
  return result;
};

// Remove a finance charge, reversing it off the invoice, customer mirror, and
// sale balance, then regenerate the PDF so it no longer shows the charge.
export const REMOVE_FINANCE_CHARGE = async (
  { dispatch },
  { projectID, saleID, invoiceID }
) => {
  const result = await call("removeFinanceCharge", {
    projectID,
    saleID,
    invoiceID,
  });
  await dispatch("GET_INVOICE", { projectID, saleID, invoiceID });
  await dispatch("REGENERATE_INVOICE_PDF", { projectID, saleID, invoiceID });
  return result;
};

export const SEND_INVOICE = async (
  { dispatch, state },
  { invoice, recipients, replyTo, subject, message }
) => {
  const wasSent = invoice.data.sent && invoice.data.sent.date;
  let modified = new Date();
  const modifiedBy = state.user.id;
  const invoiceRef = doc(db, invoice.path);

  const to = recipients.map(
    (to) => `${state.contacts[to.contact].name} <${to.email.address}>`
  );

  if (!wasSent && recipients.length) {
    // Update sent date
    updateDoc(invoiceRef, {
      modified,
      modifiedBy,
      "sent.recipients": recipients,
      "sent.date": modified,
    });
  }

  const success = await dispatch("SEND_EMAIL", {
    to,
    cc: Object.values(state.users)
      .filter((u) => !u.disabled && u.hasRole("accounting"))
      .map((u) => u.emails[0].address),
    replyTo,
    subject,
    message,
    attachments: [
      {
        filename: invoice.document.file.name,
        href: invoice.document.file.url,
      },
    ],
  });
  if (success && recipients.length) {
    if (wasSent) {
      // Followup
      return call("invoiceFollowup", {
        projectID: invoice.projectID,
        saleID: invoice.saleID,
        invoiceID: invoice.id,
        recipients,
        date: new Date(),
      });
    } else {
      // Update delivered date and lock
      modified = new Date();
      updateDoc(invoiceRef, {
        modified,
        modifiedBy,
        "sent.delivered": new Date(),
        locked: true,
      });
    }
  }
};
